import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle, Anchor, Loader2, Send } from "lucide-react";
import GlobalHeader from "@/components/GlobalHeader";
import { saveEmail, n8nSaveEmail, n8nSaveEmailSS, savePhoneNumber, n8nSavePhone, n8nSavePhoneSS, n8nSavePhoneSlickText } from "@/lib/firebase";
import { emailSchema, phoneSchema } from "@/lib/validation";
import { usePageSEO } from "@/hooks/use-page-seo";
import boatsGraphic from "@/assets/yrd-boats-graphic.svg";

const Join = () => {
  usePageSEO({
    title: "You're In — Yacht Rock Detroit",
    description: "You're on the list for Yacht Rock Detroit 2027 early access.",
  });

  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState<"processing" | "success" | "error">("processing");
  const [errorMsg, setErrorMsg] = useState("");
  const [phone, setPhone] = useState("");
  const [phoneStatus, setPhoneStatus] = useState<"idle" | "sending" | "sent" | "error">("idle");
  const [phoneError, setPhoneError] = useState("");

  useEffect(() => {
    const email = searchParams.get("email");
    const src = searchParams.get("src") || "email-blast";

    if (!email) {
      setStatus("error");
      setErrorMsg("No email provided.");
      return;
    }

    const result = emailSchema.safeParse(email);
    if (!result.success) {
      setStatus("error");
      setErrorMsg("Invalid email address.");
      return;
    }

    const validEmail = result.data;

    Promise.all([
      saveEmail(validEmail, "email-blast").catch(console.error),
      n8nSaveEmail(validEmail, src).catch(console.error),
      n8nSaveEmailSS(validEmail, src).catch(console.error),
    ]).then(() => {
      setStatus("success");
    }).catch(() => {
      setStatus("success");
    });
  }, [searchParams]);

  const handlePhoneSubmit = async () => {
    setPhoneError("");
    const result = phoneSchema.safeParse(phone);
    if (!result.success) {
      setPhoneError(result.error.errors[0].message);
      return;
    }

    setPhoneStatus("sending");
    try {
      await Promise.all([
        savePhoneNumber(result.data, "email-blast").catch(console.error),
        n8nSavePhone(result.data, "email-blast").catch(console.error),
        n8nSavePhoneSS(result.data, "email-blast").catch(console.error),
        n8nSavePhoneSlickText(result.data).catch(console.error),
      ]);
      setPhoneStatus("sent");
    } catch {
      setPhoneStatus("sent");
    }
  };

  return (
    <div className="min-h-screen bg-yacht-blue relative overflow-hidden flex flex-col">
      {/* Noise texture */}
      <div
        className="absolute inset-0 opacity-[0.12] pointer-events-none"
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 400 400' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E")`,
        }}
      />

      {/* Boats watermark */}
      <img
        src={boatsGraphic}
        alt=""
        className="absolute pointer-events-none z-0 w-[150%] left-1/2 -translate-x-1/2 top-[-20%] opacity-10"
        style={{ filter: "brightness(0) invert(1)" }}
      />

      <div className="relative z-10 flex-1 flex items-center justify-center px-6 py-20">
        {status === "processing" && (
          <motion.div
            className="text-center"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
          >
            <Loader2 className="w-12 h-12 text-yacht-yellow animate-spin mx-auto mb-4" />
            <p className="text-white/70 text-lg">Adding you to the list...</p>
          </motion.div>
        )}

        {status === "success" && (
          <motion.div
            className="text-center max-w-lg"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ type: "spring", delay: 0.2, stiffness: 200 }}
            >
              <CheckCircle className="w-20 h-20 text-yacht-yellow mx-auto mb-6" />
            </motion.div>

            <h1 className="text-4xl md:text-6xl font-bold text-white mb-4 leading-tight">
              You're In!
            </h1>
            <p
              className="text-white/70 text-lg md:text-xl mb-3"
              style={{ fontFamily: "Georgia, serif" }}
            >
              You're on the list for Yacht Rock Detroit 2027 early access.
            </p>
            <p className="text-white/50 text-base mb-8">
              Welcome aboard — <span className="text-yacht-yellow font-semibold">you'll know first</span> when 2027 goes live.
            </p>

            {/* Phone number input — prominent, before CTAs */}
            {phoneStatus !== "sent" ? (
              <motion.div
                className="mb-12 max-w-md mx-auto bg-yacht-cream rounded-2xl p-6 md:p-8 shadow-2xl"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
              >
                <p className="text-yacht-charcoal text-lg md:text-xl font-bold mb-1">
                  Want text updates? 📲
                </p>
                <p className="text-yacht-charcoal/60 text-sm mb-5">
                  Get text updates about lineup news and exclusive announcements.
                </p>
                <input
                  type="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="(555) 123-4567"
                  className="w-full px-5 py-4 rounded-full bg-white border-2 border-yacht-charcoal/20 text-yacht-charcoal placeholder:text-yacht-charcoal/30 focus:outline-none focus:border-yacht-blue focus:ring-2 focus:ring-yacht-blue/30 text-lg mb-4"
                />
                {phoneError && (
                  <p className="text-red-500 text-sm mb-3 -mt-2">{phoneError}</p>
                )}
                <button
                  onClick={handlePhoneSubmit}
                  disabled={phoneStatus === "sending" || !phone.trim()}
                  className="w-full px-6 py-4 bg-yacht-yellow text-yacht-charcoal font-bold rounded-full hover:scale-[1.02] transition-transform shadow-lg disabled:opacity-40 disabled:hover:scale-100 flex items-center justify-center gap-2 text-lg"
                >
                  {phoneStatus === "sending" ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    <>
                      <Send className="w-5 h-5" />
                      Get Text Updates
                    </>
                  )}
                </button>
              </motion.div>
            ) : (
              <motion.div
                className="mb-12 max-w-md mx-auto"
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
              >
                <div className="bg-yacht-cream rounded-2xl p-6 md:p-8 shadow-2xl mb-6">
                  <CheckCircle className="w-8 h-8 text-yacht-teal mx-auto mb-2" />
                  <p className="text-yacht-charcoal font-bold text-lg">
                    You're all set for text updates!
                  </p>
                </div>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <button
                    onClick={() => navigate("/tickets")}
                    className="px-8 py-4 bg-yacht-yellow text-yacht-charcoal font-bold text-lg rounded-full hover:scale-105 transition-transform shadow-lg"
                  >
                    Relive 2026
                  </button>
                  <button
                    onClick={() => navigate("/")}
                    className="px-8 py-4 bg-white/10 text-white font-semibold text-lg rounded-full hover:bg-white/20 transition-colors"
                  >
                    Explore the Festival
                  </button>
                </div>
              </motion.div>
            )}

            <motion.div
              className="mt-12 flex items-center justify-center gap-2 text-white/30"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.8 }}
            >
              <Anchor className="w-4 h-4" />
              <span className="text-sm tracking-widest uppercase">Smooth Sailing Ahead</span>
            </motion.div>
          </motion.div>
        )}

        {status === "error" && (
          <motion.div
            className="text-center max-w-lg"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h1 className="text-3xl md:text-5xl font-bold text-white mb-4">
              Something went wrong
            </h1>
            <p className="text-white/60 text-lg mb-8">{errorMsg}</p>
            <button
              onClick={() => navigate("/")}
              className="px-8 py-4 bg-yacht-yellow text-yacht-charcoal font-bold text-lg rounded-full hover:scale-105 transition-transform shadow-lg"
            >
              Go to Homepage
            </button>
          </motion.div>
        )}
      </div>
    </div>
  );
};

export default Join;
